export interface InlineStyleSnapshot {
  value: string | null;
  important: boolean;
}

export function readInlineStyle(element: HTMLElement, property: string): InlineStyleSnapshot {
  const value = element.style.getPropertyValue(property) || null;
  const important = element.style.getPropertyPriority(property) === 'important';
  return { value, important };
}

export function readInlineStyles(
  element: HTMLElement,
  properties: string[]
): Record<string, InlineStyleSnapshot> {
  return properties.reduce<Record<string, InlineStyleSnapshot>>((acc, property) => {
    acc[property] = readInlineStyle(element, property);
    return acc;
  }, {});
}

export function writeInlineStyle(
  element: HTMLElement,
  property: string,
  snapshot: InlineStyleSnapshot
): void {
  if (snapshot.value === null) {
    element.style.removeProperty(property);
    return;
  }

  element.style.setProperty(property, snapshot.value, snapshot.important ? 'important' : '');
}

export function writeInlineStyles(
  element: HTMLElement,
  snapshot: Record<string, InlineStyleSnapshot>
): void {
  Object.entries(snapshot).forEach(([property, value]) => {
    writeInlineStyle(element, property, value);
  });
}

export function getScrollPosition(
  element: HTMLElement | Window = window,
  documentRef?: Document
): { x: number; y: number } {
  if (element === window) {
    const doc = documentRef ?? document;
    return {
      x: window.scrollX || doc.documentElement.scrollLeft || 0,
      y: window.scrollY || doc.documentElement.scrollTop || 0,
    };
  }

  const el = element as HTMLElement;
  return {
    x: el.scrollLeft,
    y: el.scrollTop,
  };
}

export function setScrollPosition(
  element: HTMLElement | Window = window,
  position: { x?: number; y?: number } = {}
): void {
  const { x = 0, y = 0 } = position;

  if (element === window) {
    window.scrollTo({ left: x, top: y, behavior: 'auto' });
    return;
  }

  const el = element as HTMLElement;

  if (typeof el.scrollTo === 'function') {
    el.scrollTo({ left: x, top: y, behavior: 'auto' });
  } else {
    el.scrollLeft = x;
    el.scrollTop = y;
  }
}
